import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, MailWarning, RefreshCw, Trash2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";

const Light = ({ ok }) => (
  <span className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${ok ? "bg-emerald-400" : "bg-red-500"}`}
    style={{ boxShadow: ok ? "0 0 10px rgba(52,211,153,.6)" : "0 0 10px rgba(239,68,68,.6)" }} />
);

const SERVICES = [
  ["maps_autocomplete", "Address search (Google Places)"],
  ["maps_routes", "Road miles & drive time (Google Routes)"],
  ["email", "Email delivery"],
  ["push", "App notifications"],
  ["storage", "Photo storage"],
];

/** Red or green per service, with the reason when something is down. */
export const AdminHealth = () => {
  const [data, setData] = useState(null);
  const [bounces, setBounces] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setBusy(true); setError("");
    try {
      const [health, failures] = await Promise.all([
        api.get("/admin/health/integrations"),
        api.get("/admin/health/email-failures"),
      ]);
      setData(health.data);
      setBounces(failures.data);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  useEffect(() => { load(); }, []);

  const clearBounces = async () => {
    await api.delete("/admin/health/email-failures").catch(() => {});
    load();
  };

  if (!data) {
    return <div data-testid="admin-health-loading" className="flex items-center gap-3 text-sm text-slate-400">
      {error ? <span className="text-red-400">{error}</span> : <><Loader2 className="h-4 w-4 animate-spin" /> Checking services…</>}
    </div>;
  }

  const allOk = SERVICES.every(([k]) => data[k]?.ok);

  return (
    <div data-testid="admin-health" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-xl text-slate-50">
            {allOk ? "Everything is working" : "Something needs attention"}
          </h2>
          <p className="mt-1 text-sm text-slate-400">
            Live checks against each service this app depends on, run just now.
          </p>
        </div>
        <Button data-testid="admin-health-refresh" variant="outline" size="sm" disabled={busy} onClick={load}
          className="border-ink-600 bg-transparent text-slate-300 hover:text-gold">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCw className="mr-2 h-3.5 w-3.5" /> Re-check</>}
        </Button>
      </div>

      <div className="space-y-3">
        {SERVICES.map(([key, label]) => {
          const service = data[key] || {};
          return (
            <div key={key} data-testid={`health-${key}`}
              className={`rounded-xl border p-4 ${service.ok ? "border-ink-600 bg-ink-800" : "border-red-500/30 bg-red-500/5"}`}>
              <div className="flex items-start gap-3">
                <span className="mt-1.5"><Light ok={!!service.ok} /></span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-slate-100">{label}</p>
                  {service.detail && (
                    <p data-testid={`health-${key}-detail`} className="mt-1 text-xs leading-relaxed text-slate-400">
                      {service.detail}
                    </p>
                  )}
                  {service.key_tail && (
                    <p className="mt-1 font-mono text-[10px] text-slate-600">key ending {service.key_tail}</p>
                  )}
                </div>
                <span className={`shrink-0 font-mono text-[10px] uppercase tracking-widest ${service.ok ? "text-emerald-400" : "text-red-400"}`}>
                  {service.ok ? "ok" : "down"}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {data.map_fallbacks_24h > 0 && (
        <div data-testid="health-fallback-warning" className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
          <p className="flex items-center gap-2 text-sm text-slate-100">
            <AlertTriangle className="h-4 w-4 text-amber-300" />
            {data.map_fallbacks_24h} quote{data.map_fallbacks_24h === 1 ? "" : "s"} in the last 24 hours used
            estimated mileage
          </p>
          <p className="mt-2 text-xs text-slate-400">
            Google could not be reached for those, so operators may have priced trips on a straight-line
            estimate instead of real road miles.
          </p>
        </div>
      )}

      <div data-testid="admin-bounce-inbox" className="rounded-xl border border-ink-600 bg-ink-800 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-gold">
            <MailWarning className="h-3 w-3" /> Nobody heard from us ({bounces?.total || 0})
          </p>
          {bounces?.total > 0 && (
            <button type="button" data-testid="admin-bounce-clear" onClick={clearBounces}
              className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-500 hover:text-red-400">
              <Trash2 className="h-3 w-3" /> clear list
            </button>
          )}
        </div>

        {!bounces?.total ? (
          <p data-testid="admin-bounce-empty" className="mt-3 flex items-center gap-2 text-sm text-slate-400">
            <CheckCircle2 className="h-4 w-4 text-emerald-400" /> Every email has reached its recipient.
          </p>
        ) : (
          <>
            <div className="mt-3 flex flex-wrap gap-2">
              {Object.entries(bounces.by_reason).map(([reason, count]) => (
                <span key={reason} className="rounded-full border border-ink-600 px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-slate-400">
                  {reason} · {count}
                </span>
              ))}
            </div>
            <div className="mt-4 space-y-2">
              {bounces.failures.map((f) => (
                <div key={f.id} data-testid="admin-bounce-row"
                  className="rounded-lg border border-ink-600 bg-ink-900/50 p-3">
                  <p className="truncate text-sm text-slate-200">{f.to}</p>
                  <p className="mt-0.5 truncate text-xs text-slate-500">{f.subject}</p>
                  <p className="mt-1.5 font-mono text-[10px] text-slate-600">
                    {String(f.at).slice(0, 16).replace("T", " ")} · status {f.status || "—"}
                  </p>
                  {f.detail && <p className="mt-1.5 break-all text-[11px] text-slate-500">{f.detail}</p>}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
